import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import {
  SplitTestCase,
  SplitTestCasesService,
} from '../split-test-cases.service';
import { WebsocketCommandType } from '../../enums/websocket-command-type.enum';
import { WebsocketSignalRService } from '../websocket-signalr.service';
import { AnalyticsService } from '../analytics/analytics.service';
import { SettingsDataService } from './settingsData.service';
import { ChatType } from '../../types/chat-data.interface';
import { RouteName } from '../../enums/route-name.enum';
import { UserDataService } from './userData.service';
import { ModalsService } from '../modals.service';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { OnboardingModalComponent } from '../../components/tutorial/onboarding-modal/onboarding-modal.component';
import { environment } from 'src/environments/environment';
import { BuildVersion } from 'src/environments/environment-model.interface';
import { CreationGirlService } from '../creation-girl.service';

const tutorialSteps = [
  'shower_start',
  'enter_name',
  '18_years',
  'policy',
  'my_girls',
  'photos',
  'tokens',
  'chat',
  'click-gift',
  'send-gift',
  'chat-finish',
  'first-swipe-visit',
  'create-girl',
] as const;

export type TutorialStep = (typeof tutorialSteps)[number];

export const additionalChatTutorialKey = 'additional_chat_tutorial_completed';

@Injectable({
  providedIn: 'root',
})
export class TutorialService {
  public currentStep$: BehaviorSubject<TutorialStep> = new BehaviorSubject(
    null,
  );
  public isActive$ = this.currentStep$.pipe(map((step) => !!step));
  public isAnswersVisible$ = new BehaviorSubject(false);

  private readonly _localStorageKey = 'tutorialStep';
  private _initialized: boolean;
  private _tutorialSteps: TutorialStep[] = [];

  private _tutorialChatId: string;

  public get isActive() {
    return this.currentStep$.value !== null;
  }

  constructor(
    private _websocketSignalRService: WebsocketSignalRService,
    private _splitTestCasesService: SplitTestCasesService,
    private _settingsDataService: SettingsDataService,
    private _analyticsService: AnalyticsService,
    private _userDataService: UserDataService,
    private _modalsService: ModalsService,
    private _router: Router,
    private _creationGirlService: CreationGirlService,
  ) {}

  public check(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (!this._initialized) {
      this.init();
      this._initialized = true;
    }

    if (!this.currentStep$.value) {
      return true;
    }

    if (!this._tutorialChatId) {
      const tutorialChat =
        this._userDataService.updateUserData.value.chats.find(
          (c) => c.type === ChatType.tutorial,
        );
      this._tutorialChatId = tutorialChat?.id;
    }

    const chatUrl = `${RouteName.allChats}/${this._tutorialChatId}/messages`;
    const giftsUrl = `${RouteName.allChats}/${this._tutorialChatId}/gifts`;

    switch (this.currentStep$.value) {
      case 'shower_start':
      case 'enter_name':
      case '18_years':
      case 'policy':
      case 'my_girls':
      case 'photos':
      case 'tokens':
        this._modalsService.openModal(OnboardingModalComponent).subscribe();
        if (state.url !== `/${RouteName.swipingChatCards}`) {
          return this._router.parseUrl(RouteName.swipingChatCards);
        }
        return false;
      case 'chat':
      case 'chat-finish':
        if (state.url !== `/${chatUrl}`) {
          return this._router.parseUrl(chatUrl);
        }
        return true;
      case 'click-gift':
        if (state.url !== `/${chatUrl}` && state.url !== `/${giftsUrl}`) {
          return this._router.parseUrl(chatUrl);
        }
        return true;
      case 'send-gift':
        if (state.url !== `/${giftsUrl}`) {
          return this._router.parseUrl(giftsUrl);
        }
        return true;
      case 'first-swipe-visit':
        if (state.url !== `/${RouteName.swipingChatCards}`) {
          return this._router.parseUrl(RouteName.swipingChatCards);
        }
        return true;
      case 'create-girl':
        this._creationGirlService.tryOpenPopup();
        return false;
    }
  }

  public getStepIndex(step: TutorialStep) {
    return this._tutorialSteps.indexOf(step);
  }

  public setStep(step: TutorialStep, oldStepAdditionalData = '') {
    if (this.currentStep$.value === step) return;

    console.log(`set tutorial step: ${step}`);

    // if (this.currentStep$.value !== null) {
    //   this._analyticsService.tutorialStepCompleted(
    //     `${this.currentStep$.value}${
    //       oldStepAdditionalData ? `_${oldStepAdditionalData}` : ''
    //     }`,
    //   );
    // }

    this.currentStep$.next(step);
    localStorage.setItem(this._localStorageKey, step);
  }

  public after(current: TutorialStep, step: TutorialStep): boolean {
    return (
      this._tutorialSteps.indexOf(current) > this._tutorialSteps.indexOf(step)
    );
  }

  public before(current: TutorialStep, step: TutorialStep): boolean {
    return (
      this._tutorialSteps.indexOf(current) < this._tutorialSteps.indexOf(step)
    );
  }

  public finishTutorial(): Observable<void> {
    if (!this.isActive) return of(null);

    return this._websocketSignalRService
      .invoke(WebsocketCommandType.setTutorialPassed, {})
      .pipe(
        map(() => {
          this.setStep(null);
          this._analyticsService.tutorialCompleted();
        }),
      );
  }

  public setAiChatId(id: string) {
    this._tutorialChatId = id;
  }

  private init() {
    const isActive =
      !this._settingsDataService.updateSettingsData$.value.tutorialIsPassed;

    if (!isActive) {
      return;
    }
    this._tutorialSteps = tutorialSteps as any;

    let savedStep = localStorage.getItem(this._localStorageKey) as TutorialStep;

    if (!this._tutorialSteps.includes(savedStep)) {
      savedStep = null;
    }

    const nutakuStep: TutorialStep = this._splitTestCasesService.hasCase(
      SplitTestCase.swipeTutorial,
    )
      ? 'first-swipe-visit'
      : this._splitTestCasesService.hasCase(SplitTestCase.customAiTutorial)
        ? 'create-girl'
        : 'chat';

    const defaultStep =
      environment.buildVersion === BuildVersion.nutaku
        ? nutakuStep
        : this._tutorialSteps[0];

    this.setStep(savedStep ?? defaultStep);
  }
}
